import { FlatTreeControl } from '@angular/cdk/tree';
import type { DataTypeOption } from '@app/core/shared/models/data-type';
import { Hierarchy } from '@models/hierarchy';
import { PropertyValue } from '@models/property-value';

export const isAssetTree = (
  assetTree: AssetTree | AssetTreeChild | AssetTreeChildSimple
): assetTree is AssetTree => (assetTree as AssetTree).propertyValueId !== undefined;

export interface AssetTree {
  propertyValueId: number;
  componentId: number;
  description: string;
  hierarchy: HierarchySimple;
  installationType: string;
  lastUpdatedDate: Date;
  lastUpdatedUser: number;
  name: string;
  orderNumber: number;
  parent: Parent;
  installation?: Parent;
  managementOfChangeId?: number;
  integralId?: string;
}

export interface HierarchySimple {
  descriptionPrompt: string;
  hierarchyId: number;
  hierarchyTypeName: string;
  level: number;
  name: string;
}

export interface Parent {
  componentId: number;
  description: string;
  name: string;
  orderNumber: number;
  hierarchyName: string;
  level?: number;
}

export interface TreeChildBase {
  componentId: number;
  name: string;
}

export interface AssetTreeChild extends TreeChildBase {
  childCount: number;
  childHierarchyName?: string;
  description: string;
  hierarchy: Hierarchy;
  hierarchyTypeName: string;
  lastUpdatedDate?: Date;
  lastUpdatedUser?: number;
  name: string;
  orderNumber: number;
  parentId: number;
  installationId: number;
  propertyValue?: PropertyValue;
  elementId: number;
  managementOfChangeId?: number;
  integralId?: string;
}

export interface AssetTreeChildSimple extends TreeChildBase {
  description: string;
  lastUpdatedDate?: Date;
  lastUpdatedUser?: number;
  name: string;
  orderNumber: number;
}

export interface ItemTreeItem {
  componentId: number;
  parentId: number | null;
  name: string;
  count: number;
  valid: boolean | undefined;
  description?: string;
  tag?: string;
  children: ItemTreeItem[];
  childrenLoaded: boolean;
  hierarchyId: number;
  propertyValueId: number | undefined;
  managementOfChangeId: number | undefined;
  orderNumber: number;
  indent?: number;
}

export interface ItemTreeSearch {
  searchTerm: string;
  searchTags: boolean;
  installationId?: number;
  componentIdToMove?: number;
  searchHistorical?: boolean;
}

export interface ItemTreeAncestors {
  componentId: number;
  limitToInstallation: boolean;
  componentIdToMove?: number;
}

export interface SubstructureComponentChild extends AssetTreeChild {
  checked?: boolean;
}

export interface CreateAssetTree {
  componentId?: number;
  propertyValueId?: number;
  description: string;
  elementId: number;
  hierarchyId: number;
  integralId?: number | string;
  managementOfChangeId?: number;
  name: string;
  orderNumber: number;
  parentId: number;
  historical?: boolean;
}

export interface ComponentSimple {
  componentId: number;
  name: string;
  description: string;
  parent?: ComponentSimple;
}

export interface SubstructureComponentSimple extends ComponentSimple {
  completePercent: number;
}

export interface ComponentInfo {
  groupName: string;
  groupVars: DataEntryComponentPropertyInfo[];
}

interface DataEntryComponentPropertyInfo {
  propertyDataTypeId: DataTypeOption;
  propertyName: string;
  propertyValue: string;
}

export interface ComponentWithTaskCount {
  componentId: number;
  componentName: string;
  componentType?: string;
  componentDescription: string;
  totalTaskCount: number;
  completedTaskCount: number;
  startedTaskCount: number;
  substructureId: number;
  substructureName: string;
  componentIsHistorical: boolean;
  workpackTaskOrderNumber: number;
}

export interface AssetTreeState {
  control: FlatTreeControl<AssetTreeChild>;
  dataSource: AssetTreeChild[];
  componentDetails: AssetTree;
  selectedAsset: AssetTreeChild;
  installationId?: number;
  elementId: number;
  filtered: boolean;
}

export interface AssetWithInformation {
  installationCode: string;
  installationId: number;
  installationName: string;
  isoCount: number;
  siteAnomalyCount: number;
  masterAnomalyCount: number;
  workpackCount: number;
}

export interface InspectableComponentTree {
  componentId: number;
  description: string;
  hierarchyLevel: number;
  name: string;
  parentId: number;
  childrenComponents: InspectableComponentTree[];
}

export interface InspectableComponentTreeRequestDto {
  elementId: number;
  assetId: number;
  componentIds: number[];
}

export interface ComponentInformation {
  componentId: number;
  name: string;
  description: string;
  hierarchyName: string;
  componentType: string;
  parentName: string;
}

export interface ComponentServerSide {
  MANAGEMENT_OF_CHANGE_ID: number;
  COMPONENT_ID: number;
  Navigation_Name: string;
  INSTALLATION_ID: number;
  INSTALLATION: string;
  TAG: string;
  PRIMARY_MEDIA: string;
  SECONDARY_MEDIA: string;
}

export interface ScreenUpdate {
  mutatedData: AssetTreeChild[];
  offset: number;
  targetIndex: number;
  currentIndexOfItemSelected: number;
  hasSameParentButNewIndex: boolean;
  event: any;
}

export interface UndoScreenUpdateData {
  hasSameParentButNewIndex: boolean;
  newParentId: number;
  originalParent: AssetTreeChild;
}

export interface AssetTreeFormType {
  name: string;
  description: string;
  integralId: string;
  substructureType?: string;
  historical: boolean;
}

export interface CloneAssetFormType {
  parent: number;
  name: string;
  description: string;
}

export interface SubstructureComponentSimpleDto {
  component: ComponentSimple;
  substructure: ComponentSimple;
}

export interface ReorderComponentRequest {
  landingIndex: number;
  sortDirection: 'Asc' | 'Desc';
  isRoot?: boolean;
}

export interface ReorderComponentResult {
  reorderedComponents: ReorderedComponent[];
}

export interface ReorderedComponent {
  componentId: number;
  orderNumber: number;
}

export interface MoveComponentRequest {
  parentId: number;
  reason: string;
}
export interface MoveComponentResponse {
  componentId: number;
  componentName: string;
  parentId: number;
  parentName: string;
}
